import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

const TYPE_OPTIONS = [
  { value: 'provisoire', label: 'Provisoire', bg: '#fef2f2', color: '#b91c1c', border: '#ef4444' },
  { value: 'definitive', label: 'Définitive', bg: '#fef2f2', color: '#b91c1c', border: '#ef4444' },
];

const CautionFormModal = ({ isOpen, onClose, onSubmit, caution, loading }) => {
  const [form, setForm] = useState({
    type: 'provisoire',
    amount: '',
    date_posted: '',
    date_echeance: '',
    bank_reference: '',
    notes: '',
  });

  const isEdit = !!caution;

  useEffect(() => {
    if (caution) {
      setForm({
        type: caution.type,
        amount: caution.amount,
        date_posted: caution.date_posted,
        date_echeance: caution.date_echeance || '',
        bank_reference: caution.bank_reference || '',
        notes: caution.notes || '',
      });
    } else {
      setForm({ type: 'provisoire', amount: '', date_posted: '', date_echeance: '', bank_reference: '', notes: '' });
    }
  }, [caution]);

  const handleClose = () => {
    setForm({ type: 'provisoire', amount: '', date_posted: '', date_echeance: '', bank_reference: '', notes: '' });
    onClose();
  };
  // Editable form: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(handleClose, { backdropClose: false, enabled: isOpen });

  if (!isOpen) return null;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = () => {
    if (!form.amount || (!isEdit && !form.date_posted)) return;
    onSubmit(form);
  };

  const canSubmit = form.amount && (isEdit || form.date_posted);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      {...dismiss}
    >
      <div
        style={{
          background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden',
          animation: 'cautionModalIn 0.2s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #f3f4f6',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#9ca3af' }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>
              {isEdit ? 'Modifier la caution' : 'Nouvelle caution'}
            </span>
          </div>
          <button onClick={handleClose} disabled={loading}
            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          {/* Type selector (create only) */}
          {!isEdit && (
            <div style={{ marginBottom: 16 }}>
              <FieldLabel>Type</FieldLabel>
              <div style={{ display: 'flex', gap: 8 }}>
                {TYPE_OPTIONS.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => set('type', t.value)}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 6, cursor: 'pointer',
                      border: form.type === t.value ? `2px solid ${t.border}` : '2px solid #d1d5db',
                      background: form.type === t.value ? t.bg : '#fff',
                      color: form.type === t.value ? t.color : '#374151',
                      fontSize: '0.82rem', fontWeight: form.type === t.value ? 700 : 500,
                      transition: 'all 0.15s',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Amount */}
          <div style={{ marginBottom: 16 }}>
            <FieldLabel required>Montant (MAD)</FieldLabel>
            <input
              type="number"
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              placeholder="0.00"
              min="0.01" step="0.01"
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#dc2626'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Dates */}
          {!isEdit && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <FieldLabel required>Date de dépôt</FieldLabel>
                <input
                  type="date"
                  value={form.date_posted}
                  onChange={e => set('date_posted', e.target.value)}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#dc2626'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <div>
                <FieldLabel>Date d'échéance</FieldLabel>
                <input
                  type="date"
                  value={form.date_echeance}
                  onChange={e => set('date_echeance', e.target.value)}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#dc2626'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            </div>
          )}
          {isEdit && (
            <div style={{ marginBottom: 16 }}>
              <FieldLabel>Date d'échéance</FieldLabel>
              <input
                type="date"
                value={form.date_echeance}
                onChange={e => set('date_echeance', e.target.value)}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#dc2626'}
                onBlur={e => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
          )}

          {/* Bank reference */}
          <div style={{ marginBottom: 16 }}>
            <FieldLabel>Référence bancaire</FieldLabel>
            <input
              type="text"
              value={form.bank_reference}
              onChange={e => set('bank_reference', e.target.value)}
              placeholder="N° de garantie bancaire..."
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#dc2626'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Notes */}
          <div>
            <FieldLabel>Notes</FieldLabel>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              placeholder="Remarques..."
              style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }}
              onFocus={e => e.target.style.borderColor = '#dc2626'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '14px 20px', borderTop: '1px solid #f3f4f6',
        }}>
          <button onClick={handleClose} disabled={loading}
            style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !canSubmit}
            style={{
              padding: '7px 16px', borderRadius: 6, border: 'none',
              background: (!loading && canSubmit) ? '#dc2626' : '#e5e7eb',
              color: (!loading && canSubmit) ? '#fff' : '#9ca3af',
              fontSize: '0.78rem', fontWeight: 600,
              cursor: (!loading && canSubmit) ? 'pointer' : 'default',
            }}
          >
            {loading ? 'En cours...' : isEdit ? 'Enregistrer' : 'Créer la caution'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes cautionModalIn {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

const inputStyle = {
  width: '100%', fontSize: '0.82rem', padding: '8px 12px',
  border: '1px solid #d1d5db', borderRadius: 6, outline: 'none',
  background: '#fff', color: '#111827', transition: 'border-color 0.15s',
};

const FieldLabel = ({ required, children }) => (
  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 5 }}>
    {children} {required && <span style={{ color: '#dc2626' }}>*</span>}
  </div>
);

export default CautionFormModal;
